import { type FC, useEffect, useState } from "react";
import { Dialog, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useCreateMultiplePromocodesMutation } from "@/partner/entities/promocodes";
import type { CreateMultiplePromocodesInputType } from "@/types/graphql/partner.generated";
import { PromocodesMultipleForm } from "./PromocodesMultipleForm";

interface CreateMultiplePromocodesDialogProps {
  open: boolean;
  onClose: () => void;
  /** После успешной масс-генерации контейнер закрывает диалог и выставляет
   * фильтр по созданной кампании (référence: Promocodes.js). */
  onCompleted: (campaignId: string | null) => void;
}

export const CreateMultiplePromocodesDialog: FC<
  CreateMultiplePromocodesDialogProps
> = ({ open, onClose, onCompleted }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Спека §2.7: ошибка не должна переживать закрытие/повторное открытие
  // диалога — компонент смонтирован всегда (`open` только у `Dialog`).
  useEffect(() => {
    setErrorMessage(null);
  }, [open]);

  const [createMultiplePromocodes, { loading }] =
    useCreateMultiplePromocodesMutation({
      refetchQueries: ["PromocodesList"],
      onCompleted: (data) => {
        setErrorMessage(null);
        enqueueSnackbar(t("promocodes.dialog.multipleCreated"), {
          variant: "success",
        });
        onCompleted(
          data.createMultiplePromocodes[0]?.promocodeCampaign?.id ?? null
        );
      },
      onError: (error) => {
        setErrorMessage(error.message);
      },
    });

  const handleSubmit = (input: CreateMultiplePromocodesInputType) => {
    void createMultiplePromocodes({ variables: { input } });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      scroll="body"
      fullScreen={fullScreen}
    >
      <PromocodesMultipleForm
        loading={loading}
        error={errorMessage}
        onSubmit={handleSubmit}
        onClose={onClose}
      />
    </Dialog>
  );
};
